import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

const VARIANT_CLASSES = {
  badge: 'bg-[#F0F0F0] rounded-[4px] px-2 py-1 text-xs font-bold inline-block',
  bold: 'text-xs font-bold',
  large: 'text-2xl font-bold text-[#105235]',
  inline: '',
};

const formatIndex = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n) : value;
};

/**
 * Displays IRS or IRE value with superscript + tooltip when needed.
 *
 * - No value → "ND" + superscript "t" + tooltip
 * - Expert-assigned → value + superscript "Tt" + tooltip
 * - Normal → plain value
 */
export default function RiskIndexDisplay({ value, isExpert = false, variant = 'badge', className = '', expertTooltip }) {
  const { t } = useTranslation('common');
  const baseClass = `${VARIANT_CLASSES[variant] || ''} ${className}`.trim();

  // ND case: no value (null, undefined, 0, "0.00", falsy — 0 means couldn't calculate)
  // Django DecimalField returns strings like "0.00", so we must also check Number(value) === 0
  // Hover lives on the dagger superscript only — the "ND" label itself stays
  // inert. Same pattern for the expert (††) case below.
  if (!value || Number(value) === 0) {
    return (
      <span className={baseClass}>
        {t('risk_index.nd')}
        <Tooltip title={t('risk_index.nd_tooltip')}>
          <sup className="text-[10px] ml-0.5 cursor-help">{'\u2020'}</sup>
        </Tooltip>
      </span>
    );
  }

  const displayValue = formatIndex(value);

  // Expert-assigned case
  if (isExpert) {
    return (
      <span className={baseClass}>
        {displayValue}
        <Tooltip title={expertTooltip || t('risk_index.expert_tooltip')}>
          <sup className="text-[10px] ml-0.5 cursor-help">{'\u2020\u2020'}</sup>
        </Tooltip>
      </span>
    );
  }

  // Normal value
  return <span className={baseClass}>{displayValue}</span>;
}
